// src/utils/dateFormatter.ts
import { formatTime } from './dateUtils'
export function formatNorwegianDate(dateString: string): string {
  // Use the utility function to parse the date preserving Norwegian timezone
  const date = extractNorwegianDate(dateString)
  const formatter = new Intl.DateTimeFormat('no-NO', {
    day: '2-digit',  // This ensures leading zeros
    month: 'short',
  })
  return formatter.format(date).replace('.', '').toLowerCase()
}

// Extracts a date from a timestamp while preserving Norwegian timezone
export function extractNorwegianDate(timestamp: string): Date {
  if (!timestamp) {
    return new Date();
  }
  
  try {
    let result: Date;
    
    // For timestamps from API that include timezone information (e.g., 2025-05-20T19:30:00+0200)
    if (timestamp.includes('T') && (timestamp.includes('+') || timestamp.includes('Z') || timestamp.includes('-'))) {
      // Extract the date and time components directly without relying on browser's timezone conversion
      const [datePart, timePart] = timestamp.split('T');
      
      // Validate date part format
      if (!datePart || !datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
        console.error('Invalid date format:', timestamp);
        return new Date(); // Return current date as fallback
      }
      
      const [year, month, day] = datePart.split('-').map(Number);
      
      // Basic validation for date values
      if (isNaN(year) || isNaN(month) || isNaN(day) || month < 1 || month > 12 || day < 1 || day > 31) {
        console.error('Invalid date values:', { year, month, day });
        return new Date(); // Return current date as fallback
      }
      
      const timeWithoutTZ = timePart.split('+')[0].split('-')[0].split('Z')[0];
      const [hours, minutes, seconds] = timeWithoutTZ.split(':').map(Number);
      
      // Create a date in UTC and manually set the hours to match Norwegian time
      result = new Date(Date.UTC(year, month - 1, day, hours || 0, minutes || 0, seconds || 0));
    } else {
      // For timestamps without timezone info (e.g., 2025-05-20)
      result = toNorwegianTime(new Date(timestamp));
    }
    
    // Final validation to ensure we have a valid date
    if (isNaN(result.getTime())) {
      console.error('Invalid date result for timestamp:', timestamp);
      return new Date(); // Return current date as fallback
    }
    
    return result;
  } catch (error) {
    console.error('Error parsing date:', error)
    return new Date(); // Return current date as fallback
  }
}

// Helper function to convert any date to Norwegian timezone
export function toNorwegianTime(date: Date): Date {
  // Get the timezone offset difference between user's timezone and Norwegian time
  // Norway is GMT+1 (standard time) or GMT+2 (summer time)
  const norwayOffset = new Date().getTimezoneOffset() <= -120 ? 2 : 1; // Check if currently in summer time
  const userOffset = -date.getTimezoneOffset() / 60;
  const hoursDiff = norwayOffset - userOffset;
  
  // Create a new date adjusted to Norwegian time
  const result = new Date(date);
  result.setHours(date.getHours() + hoursDiff);
  return result;
}

export function formatDateRange(
  departureDate: string,
  returnDate?: string
): string {
  // Use the utility function to parse dates preserving Norwegian timezone
  const departure = extractNorwegianDate(departureDate);
  const returnD = returnDate ? extractNorwegianDate(returnDate) : null

  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
  }

  if (returnD && departure.getFullYear() !== returnD.getFullYear()) {
    options.year = 'numeric'
  }

  const departureFormatter = new Intl.DateTimeFormat('no-NO', options)
  const returnFormatter = new Intl.DateTimeFormat('no-NO', options)

  const formattedDeparture = departureFormatter.format(departure)
  if (!returnD) {
    return formattedDeparture.toLowerCase()
  }

  const formattedReturn = returnFormatter.format(returnD)
  const separator = '–'

  if (departure.getMonth() === returnD.getMonth() && departure.getFullYear() === returnD.getFullYear()) {
    // Add leading zeros to dates and keep month name lowercase
    const monthName = new Intl.DateTimeFormat('no-NO', { month: 'short' }).format(departure).toLowerCase()
    const departureDay = departure.getDate().toString().padStart(2, '0')
    const returnDay = returnD.getDate().toString().padStart(2, '0')
    return `${departureDay}.${separator}${returnDay}. ${monthName}`
  } else {
    return `${formattedDeparture.toLowerCase()} ${separator} ${formattedReturn.toLowerCase()}`
  }
}

export function formatShortNorwegianDate(dateString: string): string {
  if (!dateString) {
    return ''
  }
  
  try {
    // Use the utility function to parse the date preserving Norwegian timezone
    const date = extractNorwegianDate(dateString)
    
    // Check if date is valid before formatting
    if (isNaN(date.getTime())) {
      console.error('Invalid date encountered:', dateString)
      return ''
    }
    
    const day = date.getDate().toString().padStart(2, '0')
    const monthName = new Intl.DateTimeFormat('no-NO', { month: 'short' }).format(date).toLowerCase()
    
    return `${day}. ${monthName}`
  } catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
}

export function formatTime(timestamp: string): string {
  if (!timestamp) return ''
  
  // Extract time directly from API timestamp string to avoid any timezone conversion issues
  if (timestamp.includes('T') && (timestamp.includes('+') || timestamp.includes('Z') || timestamp.includes('-'))) {
    // Parse time portion directly from string - API times are already in Norwegian time
    const timePart = timestamp.split('T')[1];
    const timeWithoutTZ = timePart.split('+')[0].split('-')[0].split('Z')[0];
    const [hours, minutes] = timeWithoutTZ.split(':');
    // Return hours without leading zero, minutes with leading zero
    return `${parseInt(hours, 10)}:${minutes.padStart(2, '0')}`;
  }
  
  // For timestamps without timezone info, use the Norwegian conversion
  const date = toNorwegianTime(new Date(timestamp))
  const hours = date.getHours().toString().replace(/^0/, '')  // Keep this as is - hours without leading zero
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

export function formatDate(dateTime: string | undefined): string {
  if (!dateTime) return ''
  
  
  // Extract the date part directly from the string without any timezone conversion
  // For format like "2025-06-18T00:00:00+0200" or "2025-06-18"
  const datePart = dateTime.split('T')[0]
  
  const [year, month, day] = datePart.split('-').map(Number)
  console.log('parsed parts:', year, month, day)
  
  // IMPORTANT: Instead of using Date objects which can be affected by timezone,
  // we'll manually format the date using the day and month values directly
  
  // Get the Norwegian month name for the given month number (1-12)
  const monthNames = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 
                      'juli', 'august', 'september', 'oktober', 'november', 'desember']
  const monthName = monthNames[month - 1] // month is 1-based, array is 0-based
  
  // Format as "24. juni" - day with period followed by month name
  const result = `${day}. ${monthName}`
  return result
}



/**
 * Formats a date with departure and arrival times in the format "13. juni 10:30-16:20"
 * @param dateTime The date string
 * @param departureTimestamp The departure timestamp
 * @param arrivalTimestamp The arrival timestamp
 * @returns Formatted date and time string
 */
export function formatDateWithTime(dateTime: string | undefined, departureTimestamp: string, arrivalTimestamp: string): string {
  if (!dateTime || !departureTimestamp || !arrivalTimestamp) return ''
  
  
  // Instead of using service_schedule_date, let's use the departure timestamp date part
  // This ensures we show the actual day of departure, not the schedule date
  const departureDatePart = departureTimestamp.split('T')[0]
  
  const date = formatDate(departureDatePart)
  const departureTime = formatTime(departureTimestamp)
  const arrivalTime = formatTime(arrivalTimestamp)
  
  const result = `${date} ${departureTime}-${arrivalTime}`
  
  return result
}
